import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Menu,
  MenuItem,
  Divider,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
  Grid,
  Card,
  CardContent,
  CardActions,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  Preview as PreviewIcon,
  ContentCopy as CopyIcon,
  ContentCopy as ContentCopyIcon,
  Widgets as WidgetsIcon,
  ViewModule as ViewModuleIcon,
  List as ListIcon,
  Build as DesignIcon
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import widgetService from './services/widgetService';
import IconSelector from './components/IconSelector';
import { widgetComponents } from './widgets';

const WidgetManagement = () => {
  const history = useHistory();
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuWidgetId, setMenuWidgetId] = useState(null);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  const [currentTab, setCurrentTab] = useState(0); // 0: All, 1: Public, 2: Custom
  const [iconSelectorOpen, setIconSelectorOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    widgetName: '',
    widgetType: '',
    category: 'general',
    description: '',
    isPublic: false,
    icon: '',
    widgetConfig: '{}'
  });

  const categories = [
    'indicators', 'controls', 'charts', 'shapes', 'display', 'input', 'custom', 'general'
  ];

  const widgetTypes = Object.keys(widgetComponents).filter(type =>
    !type.includes('-') || type === 'text-label' || type === 'text-box' ||
    type === 'led-indicator' || type === 'progress-bar' || type === 'toggle-switch' ||
    type === 'trend-chart' || type === 'combo-box' || type === 'button-menu' ||
    type === 'set-value' || type === 'set-value-input' || type === 'led-label' ||
    type === 'rmms-converted'
  );

  useEffect(() => {
    const loadWidgets = async () => {
      try {
        setLoading(true);
        const list = await widgetService.getCustomWidgets(null, true); // Use companyId from localStorage
        console.debug('Custom Widgets loaded:', list);
        setWidgets(list || []);
      } catch (error) {
        console.error('Error loading widgets:', error);
        setSnackbar({
          open: true,
          message: 'Error loading widgets',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    loadWidgets();
  }, []);

  const handleOpenDialog = useCallback((widget = null) => {
    if (widget) {
      setFormData({
        widgetName: widget.widgetName,
        widgetType: widget.widgetType,
        category: widget.category,
        description: widget.description || '',
        isPublic: widget.isPublic,
        icon: widget.icon || '',
        widgetConfig: widget.widgetConfig || '{}'
      });
      setSelectedWidget(widget);
    } else {
      setFormData({
        widgetName: '',
        widgetType: '',
        category: 'general',
        description: '',
        isPublic: false,
        icon: '',
        widgetConfig: '{}'
      });
      setSelectedWidget(null);
    }
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedWidget(null);
  };

  const handleSave = async () => {
    try {
      let savedWidget;
      if (selectedWidget) {
        // Update existing widget
        savedWidget = await widgetService.updateCustomWidget(selectedWidget.widgetId, {
          ...formData,
          companyId: 1
        });
      } else {
        // Create new widget
        savedWidget = await widgetService.createCustomWidget({
          ...formData,
          companyId: 1
        });
      }

      // Refresh widget list
      const updatedList = await widgetService.getCustomWidgets(null, true);
      setWidgets(updatedList || []);

      setSnackbar({
        open: true,
        message: selectedWidget ? 'Widget updated successfully' : 'Widget created successfully',
        severity: 'success'
      });

      handleCloseDialog();
    } catch (error) {
      console.error('Error saving widget:', error);
      setSnackbar({
        open: true,
        message: `Error ${selectedWidget ? 'updating' : 'creating'} widget`,
        severity: 'error'
      });
    }
  };

  const handleDelete = async () => {
    try {
      if (selectedWidget) {
        await widgetService.deleteCustomWidget(selectedWidget.widgetId);
        
        // Refresh widget list
        const updatedList = await widgetService.getCustomWidgets(1, true);
        setWidgets(updatedList || []);

        setSnackbar({
          open: true,
          message: 'Widget deleted successfully',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error deleting widget:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting widget',
        severity: 'error'
      });
    } finally {
      setDeleteConfirmOpen(false);
      setSelectedWidget(null);
    }
  };

  const handleMenuClick = (event, widgetId) => {
    setAnchorEl(event.currentTarget);
    setMenuWidgetId(widgetId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuWidgetId(null);
  };

  const handleEdit = (widget) => {
    handleOpenDialog(widget);
    handleMenuClose();
  };

  const handleDesign = (widget) => {
    history.push(`/axisWidgetDesigner/${widget.widgetId}`);
    handleMenuClose();
  };

  const handleDeleteConfirm = (widget) => {
    setSelectedWidget(widget);
    setDeleteConfirmOpen(true);
    handleMenuClose();
  };

  const handleDuplicate = async (widget) => {
    try {
      const duplicateData = {
        ...widget,
        widgetName: `${widget.widgetName} (Copy)`,
        companyId: 1
      };
      delete duplicateData.widgetId;
      delete duplicateData.createdAt;
      delete duplicateData.createdBy;

      await widgetService.createCustomWidget(duplicateData);
      
      // Refresh widget list
      const updatedList = await widgetService.getCustomWidgets(null, true);
      setWidgets(updatedList || []);

      setSnackbar({
        open: true,
        message: 'Widget duplicated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error duplicating widget:', error);
      setSnackbar({
        open: true,
        message: 'Error duplicating widget',
        severity: 'error'
      });
    }
    handleMenuClose();
  };

  const filterWidgets = () => {
    let filtered = widgets;
    
    switch (currentTab) {
      case 1: // Public
        filtered = widgets.filter(w => w.isPublic);
        break;
      case 2: // Custom
        filtered = widgets.filter(w => !w.isPublic);
        break;
      default: // All
        break;
    }
    
    return filtered;
  };

  const getCategoryColor = (category) => {
    const colors = {
      indicators: '#4caf50',
      controls: '#2196f3', 
      charts: '#ff9800',
      shapes: '#9c27b0',
      display: '#607d8b',
      input: '#795548',
      custom: '#f44336',
      general: '#757575'
    };
    return colors[category] || '#757575';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading widgets...</Typography>
      </Box>
    );
  }

  const filteredWidgets = filterWidgets();

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Widget Library
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage reusable widget templates for your SCADA pages
          </Typography>
        </Box>
        
        <Box display="flex" gap={1}>
          <IconButton 
            onClick={() => setViewMode(viewMode === 'cards' ? 'table' : 'cards')}
            title={viewMode === 'cards' ? 'Switch to table view' : 'Switch to card view'}
          >
            {viewMode === 'cards' ? <ListIcon /> : <ViewModuleIcon />}
          </IconButton>
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => history.push('/axisWidgetDesigner')}
          >
            New Widget
          </Button>
        </Box>
      </Box>

      <Paper sx={{ mb: 2 }}>
        <Tabs 
          value={currentTab} 
          onChange={(e, newValue) => setCurrentTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={`All Widgets (${widgets.length})`} />
          <Tab label={`Public Templates (${widgets.filter(w => w.isPublic).length})`} />
          <Tab label={`Custom Widgets (${widgets.filter(w => !w.isPublic).length})`} />
        </Tabs>
      </Paper>

      {filteredWidgets.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <WidgetsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No widgets found
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={2}>
            Create your first widget template to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Create Widget
          </Button>
        </Paper>
      ) : viewMode === 'cards' ? (
        <Grid container spacing={2}>
          {filteredWidgets.map((widget) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={widget.widgetId}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="between" alignItems="flex-start" mb={1}>
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                      {widget.widgetName}
                    </Typography>
                    <IconButton 
                      size="small"
                      onClick={(e) => handleMenuClick(e, widget.widgetId)}
                    >
                      <MoreIcon />
                    </IconButton>
                  </Box>
                  
                  <Box display="flex" gap={1} mb={2}>
                    <Chip 
                      label={widget.category}
                      size="small"
                      sx={{ 
                        backgroundColor: getCategoryColor(widget.category),
                        color: 'white'
                      }}
                    />
                    <Chip 
                      label={widget.widgetType}
                      size="small"
                      variant="outlined"
                    />
                    {widget.isPublic && (
                      <Chip 
                        label="Public"
                        size="small"
                        color="primary"
                      />
                    )}
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40 }}>
                    {widget.description || 'No description'}
                  </Typography>
                </CardContent>
                
                <CardActions>
                  <Button 
                    size="small" 
                    startIcon={<DesignIcon />}
                    onClick={() => handleDesign(widget)}
                    variant="contained"
                    sx={{ mr: 1 }}
                  >
                    Design
                  </Button>
                  <Button 
                    size="small" 
                    startIcon={<EditIcon />}
                    onClick={() => handleEdit(widget)}
                  >
                    Properties
                  </Button>
                  <Button 
                    size="small" 
                    startIcon={<ContentCopyIcon />}
                    onClick={() => handleDuplicate(widget)}
                  >
                    Copy
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Public</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredWidgets.map((widget) => (
                <TableRow key={widget.widgetId}>
                  <TableCell>
                    <Typography variant="subtitle2">
                      {widget.widgetName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={widget.widgetType}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={widget.category}
                      size="small"
                      sx={{ 
                        backgroundColor: getCategoryColor(widget.category),
                        color: 'white'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {widget.description || 'No description'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {widget.isPublic ? (
                      <Chip label="Public" size="small" color="primary" />
                    ) : (
                      <Chip label="Private" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {widget.createdAt ? new Date(widget.createdAt).toLocaleDateString() : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton 
                      onClick={(e) => handleMenuClick(e, widget.widgetId)}
                      size="small"
                    >
                      <MoreIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleDesign(widgets.find(w => w.widgetId === menuWidgetId))}>
          <DesignIcon sx={{ mr: 1 }} fontSize="small" />
          Design Widget
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleEdit(widgets.find(w => w.widgetId === menuWidgetId))}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit Properties
        </MenuItem>
        <MenuItem onClick={() => handleDuplicate(widgets.find(w => w.widgetId === menuWidgetId))}>
          <ContentCopyIcon sx={{ mr: 1 }} fontSize="small" />
          Duplicate
        </MenuItem>
        <Divider />
        <MenuItem 
          onClick={() => handleDeleteConfirm(widgets.find(w => w.widgetId === menuWidgetId))}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedWidget ? 'Edit Widget' : 'Create New Widget'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Widget Name"
                fullWidth
                value={formData.widgetName}
                onChange={(e) => setFormData(prev => ({ ...prev, widgetName: e.target.value }))}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Widget Type</InputLabel>
                <Select
                  value={formData.widgetType}
                  label="Widget Type"
                  onChange={(e) => setFormData(prev => ({ ...prev, widgetType: e.target.value }))}
                >
                  {widgetTypes.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  label="Category"
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                >
                  {categories.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Icon"
                fullWidth
                value={formData.icon}
                onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
                placeholder="Click button to select icon"
                InputProps={{
                  endAdornment: (
                    <Button
                      size="small"
                      onClick={() => setIconSelectorOpen(true)}
                      sx={{ ml: 1 }}
                    >
                      Select Icon
                    </Button>
                  )
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Widget Configuration (JSON)"
                fullWidth
                multiline
                rows={4}
                value={formData.widgetConfig}
                onChange={(e) => setFormData(prev => ({ ...prev, widgetConfig: e.target.value }))}
                placeholder="Enter widget properties as JSON"
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isPublic}
                    onChange={(e) => setFormData(prev => ({ ...prev, isPublic: e.target.checked }))}
                  />
                }
                label="Make this widget available to all companies"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={!formData.widgetName || !formData.widgetType}
          >
            {selectedWidget ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Widget</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedWidget?.widgetName}"? 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Icon Selector Dialog */}
      <IconSelector
        open={iconSelectorOpen}
        onClose={() => setIconSelectorOpen(false)}
        onSelect={(iconName) => setFormData(prev => ({ ...prev, icon: iconName }))}
        currentIcon={formData.icon}
        widgetType={formData.widgetType}
      />
    </Box>
  );
};

export default WidgetManagement;